import { useState } from "react";
import { Camera, MessageCircle, Trash2, UserRound } from "lucide-react";
import { Avatar } from "../components/Avatar";
import { ResendVerification } from "./Security";
import { api, errMsg } from "../lib/api";

const VerifyPill = ({ ok, testId }) => <span className={ok ? "pill ok" : "pill warn"} data-testid={testId}>{ok ? "Verified" : "Non verified"}</span>;

const WhatsAppVerify = ({ user, onUser }) => {
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) { setMsg(errMsg(err, "Gagal, coba lagi")); } finally { setBusy(false); }
  };
  const send = () => run(async () => { setMsg((await api.post("/profile/whatsapp/send-otp")).data.message); setSent(true); });
  const verify = () => run(async () => { setMsg((await api.post("/profile/whatsapp/verify", { code })).data.message); onUser((await api.get("/profile")).data); });
  if (user.phone_verified) return null;
  return (
    <div className="otp-box">
      <div className="row-actions">
        <button type="button" className="outline sm" disabled={busy} onClick={send} data-testid="whatsapp-send-otp-button"><MessageCircle size={13} /> {sent ? "Kirim ulang kode" : "Kirim kode via WhatsApp"}</button>
        {sent && (
          <>
            <input className="inline-input" maxLength={6} inputMode="numeric" placeholder="6 digit" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="whatsapp-otp-input" />
            <button type="button" className="primary sm" disabled={busy || code.length !== 6} onClick={verify} data-testid="whatsapp-verify-button">Verifikasi</button>
          </>
        )}
      </div>
      {msg && <small className="muted block" data-testid="whatsapp-otp-message">{msg}</small>}
    </div>
  );
};

const AvatarEditor = ({ user, onUser }) => {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return setErr("Ukuran foto maksimal 3MB");
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    setErr("");
    try { onUser((await api.post("/profile/avatar", body)).data); } catch (x) { setErr(errMsg(x, "Foto gagal diunggah")); } finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async () => {
    try { onUser((await api.delete("/profile/avatar")).data); } catch (x) { setErr(errMsg(x, "Foto gagal dihapus")); }
  };
  return (
    <div className="avatar-editor">
      <Avatar avatarId={user.avatar_id} name={user.alias || user.name} className="avatar-lg" testId="profile-avatar" />
      <div>
        <label className="outline sm upload-btn"><Camera size={13} /> {busy ? "Mengunggah…" : "Ganti foto"}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} data-testid="profile-avatar-input" /></label>
        {user.avatar_id && <button type="button" className="text-button" onClick={remove} data-testid="profile-avatar-remove"><Trash2 size={12} /> Hapus foto</button>}
        <small className="muted block">JPG/PNG/WEBP, maks. 3MB. Tampil di sidebar & leaderboard.</small>
        {err && <small className="down block" data-testid="profile-avatar-error">{err}</small>}
      </div>
    </div>
  );
};

export default function Profile({ user, onUser }) {
  const [form, setForm] = useState({ name: user.name || "", alias: user.alias || "", phone: user.phone || "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    setError("");
    try {
      const r = await api.put("/profile", { name: form.name, alias: form.alias, phone: form.phone });
      onUser(r.data);
      setMsg("Profil tersimpan.");
    } catch (err) {
      setError(errMsg(err, "Profil gagal disimpan"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="page-intro">
        <div><p className="eyebrow">AKUN</p><h2>Pengaturan profil</h2><p className="muted">Nama lengkap dan nama samaran wajib diisi dan bisa diubah kapan saja.</p></div>
      </div>
      <form className="panel integration-card profile-card" onSubmit={save} data-testid="profile-form">
        <div className="panel-head"><div><p className="eyebrow">DATA DIRI</p><h3>{user.alias || user.name}</h3></div><UserRound size={18} /></div>
        <AvatarEditor user={user} onUser={onUser} />
        <label>Nama lengkap *<input required minLength={2} maxLength={80} value={form.name} onChange={set("name")} data-testid="profile-name-input" /></label>
        <label>Nama samaran *<input required minLength={2} maxLength={30} value={form.alias} placeholder="Ditampilkan di leaderboard" onChange={set("alias")} data-testid="profile-alias-input" /></label>
        <div className="field-row">
          <label>Email<input value={user.email} readOnly data-testid="profile-email" /></label>
          <VerifyPill ok={user.email_verified !== false} testId="profile-email-status" />
        </div>
        {user.email_verified === false && <ResendVerification testId="profile-resend-email" />}
        <div className="field-row">
          <label>Nomor WhatsApp<input required value={form.phone} onChange={set("phone")} placeholder="08xxxxxxxxxx" data-testid="profile-phone-input" /></label>
          <VerifyPill ok={!!user.phone_verified} testId="profile-whatsapp-status" />
        </div>
        {form.phone !== user.phone && <small className="muted block">Mengganti nomor akan mereset status verifikasi WhatsApp.</small>}
        {form.phone === user.phone && <WhatsAppVerify user={user} onUser={onUser} />}
        {msg && <div className="notice" data-testid="profile-success">{msg}</div>}
        {error && <div className="error" data-testid="profile-error">{error}</div>}
        <button className="primary" disabled={busy} data-testid="profile-save-button">{busy ? "Menyimpan…" : "Simpan profil"}</button>
      </form>
    </>
  );
}
